import { Dropdown, Popover, Tooltip } from 'antd';
import { cloneDeep } from 'lodash';
import React from 'react';

import { CaretRightOutlined, MoreOutlined } from '@ant-design/icons';
import { useGetMappedEvents } from '@src/api/emp/queryHooks';
import MappedEventDetail from '@src/emp-3.0/components/MappedEventDetail';
import { IEventBreakdown } from '@src/emp-3.0/types';

import { useSankeyStore } from '../../store';
import { LANDING_EVENTS_WITH_MAX_DIMENSION_RULES_COUNT } from '../constants';
import { getDefaultBreakdownEvent, getSingleBreakdownEvent } from '../utils';
import { EMPTY_ARRAY } from '@src/emp-3.0/components/custom-dimension/custom-dimension-panel/constant';
import { useGetSankeyDimensions } from '@src/emp-3.0/api/queryHooks';

export const NodeDropdown = ({ node, chartRef, onRefreshSankey }) => {
  const { data: mappedEventsData, isLoading } = useGetMappedEvents({
    preferred_version: 'deployed',
    need_all_storage: 1,
  });
  const { sankeyRequestBody, setSankeyRequestBody } = useSankeyStore((state) => state);
  const { data: sankeyDimensionList = EMPTY_ARRAY } = useGetSankeyDimensions();
  const { event_breakdown, landing_events, event_name_filters } = sankeyRequestBody;
  const isLandingEvent = landing_events[0].event_name === node.event_name;
  const eventIndex = event_breakdown.findIndex((event) => event.event_name === node.event_name);

  const onBreakdown = (e) => {
    e.stopPropagation();
    const event = new CustomEvent('SANKEY_BREAKDOWN_EVENT', { detail: node });
    document.dispatchEvent(event); // fire event to hightlight the section;
    const dimensionList = sankeyDimensionList.filter((item) => item.can_breakdown === 1);
    const defaultEventBreakdown = getDefaultBreakdownEvent(node, dimensionList);
    const cloneEventBreakdown = cloneDeep(event_breakdown);
    if (eventIndex === -1) {
      cloneEventBreakdown.push(defaultEventBreakdown);
    } else {
      if (
        cloneEventBreakdown[eventIndex].breakdowns.length >=
        LANDING_EVENTS_WITH_MAX_DIMENSION_RULES_COUNT
      )
        return;
      const singleEventBreakdown = getSingleBreakdownEvent(
        node,
        cloneEventBreakdown,
        eventIndex,
        dimensionList,
      );
      cloneEventBreakdown[eventIndex].breakdowns.push(...singleEventBreakdown);
    }
    setSankeyRequestBody({
      event_breakdown: cloneEventBreakdown,
    });
  };

  const onRemoveNode = (e) => {
    e.stopPropagation();
    if (eventIndex !== -1) return;
    const newEventFilter = event_name_filters.filter((event) => event !== node.event_name);
    setSankeyRequestBody({ event_name_filters: newEventFilter });
    onRefreshSankey({ event_name_filters: newEventFilter });
  };

  const mappedEvent = mappedEventsData?.data?.find(
    (even: IEventBreakdown) => even.event_name === node.event_name,
  );
  const eventDetail = <MappedEventDetail data={mappedEvent} loading={isLoading} />;
  const eventDetailItem = {
    label: (
      <Popover
        placement={'rightTop'}
        arrow={false}
        content={eventDetail}
        getPopupContainer={() => chartRef?.current}
        // In the case of small screens, the event detail popover of the leftmost node in paths will be obscured and not fully displayed.
        overlayInnerStyle={{ left: 5, position: 'absolute' }}
      >
        <div style={{ width: '110px' }}>
          Event Details <CaretRightOutlined style={{ marginLeft: '5px' }} />
        </div>
      </Popover>
    ),
    key: 'eventDetails',
  };

  const breakdownItem = {
    label: <div onClick={onBreakdown}>Breakdown</div>,
    key: 'breakdown',
  };

  const removeNodeItem = {
    label: (
      <Tooltip
        title={eventIndex !== -1 ? 'Please remove the event breakdown to exclude this event' : ''}
        getPopupContainer={() => chartRef?.current}
      >
        <span onClick={onRemoveNode}>Remove</span>
      </Tooltip>
    ),
    key: 'removeNode',
    disabled: eventIndex !== -1, // disable if event is already broken down
  };

  const menuItems: any = [];
  if (mappedEvent) {
    menuItems.push(eventDetailItem);
  }
  if (!isLandingEvent) {
    menuItems.push(breakdownItem);
    menuItems.push(removeNodeItem);
  }

  return (
    <Dropdown
      menu={{ items: menuItems }}
      trigger={['hover']}
      getPopupContainer={() => chartRef?.current}
    >
      <a
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <MoreOutlined />
      </a>
    </Dropdown>
  );
};
